<template>
    <AppHead :title="isTenxPlusOrigin ? 'Create an account to continue' : 'Register'" />
    <div class="w-full max-w-sm mx-auto">
        <div class="relative">
            <div v-if="step === 1" :key="`step-0`">
                <h1 class="text-3xl text-gray-800 text-center font-bold mb-8">
                    {{ isTenxPlusOrigin ? 'Create an account to continue' : 'Register' }}
                </h1>

                <Sso />

                <OrActionBanner action="register" />

                <div class="relative">
                    <form
                        action="/register"
                        method="post"
                        class="hs-form-register"
                        data-hs-cf-bound="true"
                        @submit.prevent="registerStore.register()"
                        @keyup.enter.prevent="registerStore.register()"
                    >
                        <RegisterFields class="mb-6" />
                        <RegisterCheckboxes class="mb-6" />
                        <Errors :errors="errors" class="mb-4" />

                        <ActionButton
                            @clicked="registerStore.register()"
                            label="Register"
                            :loading="loading"
                            :useEnter="true"
                            classes="btn-10x-orange text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium w-full"
                        />

                        <div class="pt-6 text-center text-slate-800 text-sm pb-safe">
                            Already have an account?
                            <Link
                                class="text-orange-600 font-medium hover:text-orange-500 transition-colors"
                                href="/login"
                            >
                                Sign in
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            <SetupTwoFactor v-if="step === 2" :key="`step-1`" />
            <TotpTwoFactor
                v-if="step >= 3 && twoFactorType === 'totp'"
                :key="`step-2`"
            />
            <SmsTwoFactor
                v-if="step >= 3 && twoFactorType === 'sms'"
                :key="`step-3`"
            />
        </div>
        <UiModal
            title="Cancel Two Factor Authentication setup"
            :is-open="showCancel2faModal"
        >
            <template #content>
                <p class="text-slate-800 text-sm">
                    You are going to cancel Two Factor Authentication setup. You
                    can also do this later from your account settings page.
                </p>
            </template>
            <template #footer>
                <div class="flex items-center gap-4 justify-end">
                    <button
                        class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white rounded shadow border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                        @click="registerStore.setShowCancel2faModal(false)"
                    >
                        Back to setup
                    </button>
                    <ActionButton
                        @clicked="registerStore.cancelTwoFactor()"
                        label="Yes, cancel"
                        classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                    />
                </div>
            </template>
        </UiModal>
    </div>
</template>
<script setup>
import { Link, usePage } from '@inertiajs/vue3';
import AppHead from '@/Partials/AppHead.vue';
import { storeToRefs } from 'pinia';
import { useRegisterStore } from '@/store/register';
import OrActionBanner from '@/components/auth/or-action-banner.vue';
import RegisterFields from '@/components/auth/register-fields.vue';
import RegisterCheckboxes from '@/components/auth/register-checkboxes.vue';
import SetupTwoFactor from '@/components/auth/register-steps/setup-two-factor.vue';
import TotpTwoFactor from '@/components/auth/register-steps/totp-two-factor.vue';
import SmsTwoFactor from '@/components/auth/register-steps/sms-two-factor.vue';
import Sso from '@/components/auth/sso.vue';
import Errors from '@/components/errors.vue';
import ActionButton from '@/components/action-button.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import { computed, onMounted } from 'vue';

const registerStore = useRegisterStore();

const { loading, errors, twoFactorType, step, showCancel2faModal } =
    storeToRefs(registerStore);

// SUPPORT-729: the /register route resolves ?src and ?billing (either from the
// current URL or from the intended URL saved by the auth middleware) and passes
// them here. Fall back to window.location.search so a direct arrival with
// ?src=tenxplus still works before session-based intended URL exists.
const page = usePage();
const originProps = computed(() => {
    const propSrc = page.props?.src ?? null;
    const propBilling = page.props?.billing ?? null;
    if (propSrc || propBilling) {
        return { src: propSrc, billing: propBilling };
    }
    const urlParams = new URLSearchParams(window.location.search);
    const billing = urlParams.get('billing');
    return {
        src: urlParams.get('src'),
        billing: billing === 'month' || billing === 'year' ? billing : null,
    };
});
const isTenxPlusOrigin = computed(
    () =>
        originProps.value.src === 'tenxplus' &&
        (originProps.value.billing === 'month' ||
            originProps.value.billing === 'year')
);

const storeSessions = () => {
    // Get UTM parameters from URL instead of localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const utm_campaign = urlParams.get('utm_campaign');
    const utm_content = urlParams.get('utm_content');
    const utm_medium = urlParams.get('utm_medium');
    const utm_source = urlParams.get('utm_source');
    const utm_term = urlParams.get('utm_term');

    registerStore.setUtmParams({
        utm_campaign,
        utm_content,
        utm_medium,
        utm_source,
        utm_term,
    });
};

onMounted(() => {
    storeSessions();
    registerStore.setOriginContext({
        src: originProps.value.src,
        billing: originProps.value.billing,
    });
});
</script>
